import type { AgentChoice, CaptureChoice, IntentKind, IntentPayload } from './intentTypes.js';

export type { AgentChoice, CaptureChoice, IntentKind, IntentPayload } from './intentTypes.js';

/**
 * Apply a server-confirmed intent to the WASM adapter.
 * This is called when an `input:frame` is received from the match server.
 * Intents from the server are idempotent — re-applying the same intent
 * produces the same result as the initial optimistic application.
 */
export function applyIntent(
  adapter: { intent: (intent: Record<string, unknown>) => unknown },
  kind: IntentKind,
  payload: Record<string, unknown>,
): void {
  switch (kind) {
    case 'attack_city':
      (adapter.intent as (i: Record<string, unknown>) => unknown)({
        kind: 'attack_city',
        playerIndex: payload.playerIndex,
        targetCityId: payload.targetCityId,
      });
      break;
    case 'move_unit':
      (adapter.intent as (i: Record<string, unknown>) => unknown)({
        kind: 'move_unit',
        playerIndex: payload.playerIndex,
        unitId: payload.unitId,
        targetQ: payload.targetQ,
        targetR: payload.targetR,
      });
      break;
    case 'sail_ship':
      (adapter.intent as (i: Record<string, unknown>) => unknown)({
        kind: 'sail_ship',
        playerIndex: payload.playerIndex,
        unitId: payload.unitId,
        targetQ: payload.targetQ,
        targetR: payload.targetR,
      });
      break;
    case 'dock_trade':
      (adapter.intent as (i: Record<string, unknown>) => unknown)({
        kind: 'dock_trade',
        playerIndex: payload.playerIndex,
        unitId: payload.unitId,
        cityId: payload.cityId,
        food: payload.food ?? 'grain',
      });
      break;
    case 'accept_merchant':
      (adapter.intent as (i: Record<string, unknown>) => unknown)({
        kind: 'accept_merchant',
        playerIndex: payload.playerIndex,
        cityId: payload.cityId,
      });
      break;
    case 'decline_merchant':
      (adapter.intent as (i: Record<string, unknown>) => unknown)({
        kind: 'decline_merchant',
        playerIndex: payload.playerIndex,
        cityId: payload.cityId,
      });
      break;
    case 'declare_war':
      adapter.intent({
        kind: 'declare_war',
        playerIndex: payload.playerIndex,
        toPlayer: payload.toPlayer,
      });
      break;
    case 'propose_treaty':
      adapter.intent({
        kind: 'propose_treaty',
        playerIndex: payload.playerIndex,
        toPlayer: payload.toPlayer,
        treaty: payload.treaty,
      });
      break;
    case 'accept_treaty':
    case 'decline_treaty':
      adapter.intent({
        kind,
        playerIndex: payload.playerIndex,
        fromPlayer: payload.fromPlayer,
      });
      break;
    case 'propose_marriage':
      adapter.intent({
        kind: 'propose_marriage',
        playerIndex: payload.playerIndex,
        toPlayer: payload.toPlayer,
      });
      break;
    case 'accept_marriage':
    case 'decline_marriage':
      adapter.intent({
        kind,
        playerIndex: payload.playerIndex,
        fromPlayer: payload.fromPlayer,
      });
      break;
    case 'deploy_unit':
      (adapter.intent as (i: Record<string, unknown>) => unknown)({
        kind: 'deploy_unit',
        playerIndex: payload.playerIndex,
        unitId: payload.unitId,
        targetQ: payload.targetQ,
        targetR: payload.targetR,
      });
      break;
    case 'start_building':
      (adapter.intent as (i: Record<string, unknown>) => unknown)({
        kind: 'start_building',
        playerIndex: payload.playerIndex,
        cityId: payload.cityId,
        buildingId: payload.buildingId,
      });
      break;
    case 'invest_doctrine':
      (adapter.intent as (i: Record<string, unknown>) => unknown)({
        kind: 'invest_doctrine',
        playerIndex: payload.playerIndex,
        archetype: payload.archetype,
      });
      break;
    case 'set_city_doctrine':
      (adapter.intent as (i: Record<string, unknown>) => unknown)({
        kind: 'set_city_doctrine',
        playerIndex: payload.playerIndex,
        cityId: payload.cityId,
        archetype: payload.archetype,
      });
      break;
    case 'activate_hero_ability':
      (adapter.intent as (i: Record<string, unknown>) => unknown)({
        kind: 'activate_hero_ability',
        playerIndex: payload.playerIndex,
        unitId: payload.unitId,
        slotIndex: payload.slotIndex,
      });
      break;
    case 'select_hero_ability':
      (adapter.intent as (i: Record<string, unknown>) => unknown)({
        kind: 'select_hero_ability',
        playerIndex: payload.playerIndex,
        unitId: payload.unitId,
        slotIndex: payload.slotIndex,
        abilityId: payload.abilityId,
      });
      break;
    case 'set_formation':
      (adapter.intent as (i: Record<string, unknown>) => unknown)({
        kind: 'set_formation',
        playerIndex: payload.playerIndex,
        unitId: payload.unitId,
        formation: payload.formation,
      });
      break;
    case 'pilgrimage':
      (adapter.intent as (i: Record<string, unknown>) => unknown)({
        kind: 'pilgrimage',
        playerIndex: payload.playerIndex,
      });
      break;
    case 'reform':
      (adapter.intent as (i: Record<string, unknown>) => unknown)({
        kind: 'reform',
        playerIndex: payload.playerIndex,
      });
      break;
    // Server-issued in an absent seat's batch (match-server hand-over).
    case 'assume_ai_control':
    case 'release_ai_control':
      (adapter.intent as (i: Record<string, unknown>) => unknown)({
        kind,
        playerIndex: payload.playerIndex,
      });
      break;
    case 'build_bridge':
      (adapter.intent as (i: Record<string, unknown>) => unknown)({
        kind: 'build_bridge',
        playerIndex: payload.playerIndex,
        unitId: payload.unitId,
        targetQ: payload.targetQ,
        targetR: payload.targetR,
      });
      break;
    case 'dispatch_spy':
      adapter.intent({
        kind: 'dispatch_spy',
        playerIndex: payload.playerIndex,
        unitId: payload.unitId,
        targetCityId: payload.targetCityId,
      });
      break;
    case 'sabotage_city':
      adapter.intent({
        kind: 'sabotage_city',
        playerIndex: payload.playerIndex,
        unitId: payload.unitId,
        targetCityId: payload.targetCityId,
        buildingId: payload.buildingId,
        frameSeat: payload.frameSeat,
      });
      break;
    case 'assassinate':
      adapter.intent({
        kind: 'assassinate',
        playerIndex: payload.playerIndex,
        unitId: payload.unitId,
        royalId: payload.royalId,
        frameSeat: payload.frameSeat,
      });
      break;
    case 'poison_supply':
      adapter.intent({
        kind: 'poison_supply',
        playerIndex: payload.playerIndex,
        unitId: payload.unitId,
        targetCityId: payload.targetCityId,
        supply: payload.supply,
        frameSeat: payload.frameSeat,
      });
      break;
    case 'post_counter_spy':
      adapter.intent({
        kind: 'post_counter_spy',
        playerIndex: payload.playerIndex,
        unitId: payload.unitId,
        cityId: payload.cityId,
      });
      break;
    case 'recall_counter_spy':
      adapter.intent({
        kind: 'recall_counter_spy',
        playerIndex: payload.playerIndex,
        unitId: payload.unitId,
      });
      break;
    case 'sweep':
      adapter.intent({ kind: 'sweep', playerIndex: payload.playerIndex, unitId: payload.unitId });
      break;
    case 'intercept':
      adapter.intent({
        kind: 'intercept',
        playerIndex: payload.playerIndex,
        unitId: payload.unitId,
        targetUnitId: payload.targetUnitId,
      });
      break;
    case 'decide_agent':
      adapter.intent({
        kind: 'decide_agent',
        playerIndex: payload.playerIndex,
        captiveId: payload.captiveId,
        choice: payload.choice,
      });
      break;
    case 'choose_capture':
      adapter.intent({
        kind: 'choose_capture',
        playerIndex: payload.playerIndex,
        royalId: payload.royalId,
        choice: payload.choice,
      });
      break;
    case 'spy_rescue':
      adapter.intent({
        kind: 'spy_rescue',
        playerIndex: payload.playerIndex,
        unitId: payload.unitId,
        royalId: payload.royalId,
      });
      break;
    case 'raid_rescue':
      adapter.intent({
        kind: 'raid_rescue',
        playerIndex: payload.playerIndex,
        royalId: payload.royalId,
      });
      break;
    case 'pay_ransom':
      adapter.intent({
        kind: 'pay_ransom',
        playerIndex: payload.playerIndex,
        royalId: payload.royalId,
      });
      break;
    case 'choose_hero_captivity':
      adapter.intent({
        kind: 'choose_hero_captivity',
        playerIndex: payload.playerIndex,
        unitId: payload.unitId,
        choice: payload.choice,
      });
      break;
    case 'pay_hero_ransom':
      adapter.intent({
        kind: 'pay_hero_ransom',
        playerIndex: payload.playerIndex,
        unitId: payload.unitId,
      });
      break;
    case 'dispatch_hero_quest':
      adapter.intent({
        kind: 'dispatch_hero_quest',
        playerIndex: payload.playerIndex,
        unitId: payload.unitId,
        questKind: payload.questKind,
        ...(payload.amount === undefined ? {} : { amount: payload.amount }),
        ...(payload.targetId === undefined ? {} : { targetId: payload.targetId }),
      });
      break;
    case 'summon_legend':
      adapter.intent({
        kind: 'summon_legend',
        playerIndex: payload.playerIndex,
        cityId: payload.cityId,
      });
      break;
    case 'activate_legend_ability':
      adapter.intent({
        kind: 'activate_legend_ability',
        playerIndex: payload.playerIndex,
        unitId: payload.unitId,
        slot: payload.slot,
        targetQ: payload.targetQ,
        targetR: payload.targetR,
      });
      break;
    default:
      throw new Error(`unknown intent kind: ${String(kind)}`);
  }
}

/**
 * Apply a server `input:frame` bundle to the adapter.
 *
 * Wire format (match-server `LockstepLoop`): `{ "<seat>": intent[] }`, seats in
 * ascending order, each intent carrying its own `kind` and the server-stamped
 * `playerIndex`. The legacy `{ "<kind>": intent[] }` shape is still accepted
 * (keys that are not seat numbers), so older recordings and tests replay.
 */
export function applyFrame(
  adapter: { intent: (intent: Record<string, unknown>) => unknown },
  frame: Record<string, readonly unknown[]>,
): void {
  const keys = Object.keys(frame);
  const seated = keys.length > 0 && keys.every((k) => /^\d+$/.test(k));
  if (seated) {
    for (const seat of keys.map(Number).sort((a, b) => a - b)) {
      for (const intent of frame[String(seat)] ?? []) {
        const i = intent as Record<string, unknown>;
        applyIntent(adapter, i.kind as IntentKind, { ...i, playerIndex: seat });
      }
    }
    return;
  }
  for (const [kind, intents] of Object.entries(frame)) {
    for (const intent of intents) {
      applyIntent(adapter, kind as IntentKind, intent as Record<string, unknown>);
    }
  }
}
